import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRemoteAssist } from '../contexts/RemoteAssistContext';
import { Mic, MicOff, Video, VideoOff, PhoneOff, Maximize2, Expand, Shrink, Camera } from 'lucide-react';
import useDraggable from '../hooks/useDraggable';

function formatTime(s) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

const MIN_W = 200;
const MAX_W = 640;
const DEFAULT_W = 280;

export default function RemoteAssistBubble() {
  const {
    activeSession, state, remoteStream, remoteScreenStream, elapsed,
    isMuted, isVideoOff, clientMuted, clientScreenSharing,
    availableCameras, selectedCameraId, switchCamera,
    toggleMic, toggleVideo, endSession,
  } = useRemoteAssist();
  const [showCamPicker, setShowCamPicker] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const bubbleRef = useRef(null);

  // Draggable
  const { pos: dragPos, isDragging, handlers, elementRef } = useDraggable('remote-assist-pos', { defaultZone: 'top-right', width: 280, height: 220 });

  // Resizable state
  const [width, setWidth] = useState(DEFAULT_W);
  const [isExpanded, setIsExpanded] = useState(false);
  const resizingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(DEFAULT_W);

  // Responsive: clamp width to viewport on mobile
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const effectiveMaxW = isMobile ? Math.min(MAX_W, window.innerWidth - 32) : MAX_W;
  const effectiveWidth = Math.min(width, effectiveMaxW);

  // Show screen share in bubble if available, otherwise webcam
  const displayStream = (remoteScreenStream && clientScreenSharing) ? remoteScreenStream : remoteStream;

  useEffect(() => {
    if (videoRef.current && displayStream) {
      videoRef.current.srcObject = displayStream;
    }
  }, [displayStream, state]);

  // Resize by dragging top-left corner
  const onResizeStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    resizingRef.current = true;
    startXRef.current = e.clientX;
    startWidthRef.current = effectiveWidth;

    const onMove = (ev) => {
      if (!resizingRef.current) return;
      const delta = startXRef.current - ev.clientX;
      const maxW = isMobile ? Math.min(MAX_W, window.innerWidth - 32) : MAX_W;
      const newW = Math.min(maxW, Math.max(MIN_W, startWidthRef.current + delta));
      setWidth(newW);
    };
    const onUp = () => {
      resizingRef.current = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [effectiveWidth, isMobile]);

  // Toggle expand/compact
  const toggleExpand = useCallback(() => {
    if (isExpanded) {
      setWidth(DEFAULT_W);
      setIsExpanded(false);
    } else {
      const maxW = isMobile ? Math.min(MAX_W, window.innerWidth - 32) : MAX_W;
      setWidth(maxW);
      setIsExpanded(true);
    }
  }, [isExpanded, isMobile]);

  // Use CSS visibility instead of unmounting — keeps <video> srcObject alive across navigations
  const isOnViewerPage = location.pathname.startsWith('/remote-assist/') && location.pathname !== '/remote-assist';
  const shouldShow = activeSession && state !== 'idle' && !isOnViewerPage && (state === 'connected' || state === 'waiting');

  const videoHeight = Math.round(effectiveWidth * 9 / 16);

  // Always render when session active (even hidden) so video element stays mounted
  if (!activeSession || state === 'idle') return null;

  const setBubbleRef = (el) => {
    bubbleRef.current = el;
    elementRef(el);
  };

  return (
    <div
      ref={setBubbleRef}
      style={{
        position: 'fixed',
        left: dragPos.x, top: dragPos.y, right: 'auto', bottom: 'auto',
        zIndex: 999,
        width: effectiveWidth, borderRadius: 16, overflow: 'visible',
        background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)',
        boxShadow: isDragging
          ? '0 12px 40px rgba(0,0,0,0.6)'
          : '0 8px 32px rgba(0,0,0,0.5)',
        transition: resizingRef.current || isDragging ? 'none' : 'left 0.18s ease, top 0.18s ease, width 0.2s ease, opacity 0.15s ease, box-shadow 0.15s ease',
        opacity: shouldShow ? 1 : 0,
        pointerEvents: shouldShow ? 'auto' : 'none',
        touchAction: 'none',
      }}
    >
      {/* Drag handle bar at top */}
      <div
        {...handlers}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          height: 20, cursor: isDragging ? 'grabbing' : 'grab',
          borderRadius: '16px 16px 0 0', background: 'rgba(255,255,255,0.05)',
        }}
      >
        <div style={{
          width: 32, height: 3, borderRadius: 2,
          background: 'rgba(255,255,255,0.2)',
        }} />
      </div>

      {/* Resize handle — top-left corner */}
      <div
        onMouseDown={onResizeStart}
        style={{
          position: 'absolute', top: -2, left: -2, width: 16, height: 16,
          cursor: 'nw-resize', zIndex: 10, borderRadius: '8px 0 0 0',
        }}
        title="Redimensionar"
      >
        <div style={{
          position: 'absolute', top: 4, left: 4,
          width: 8, height: 8, borderTop: '2px solid rgba(255,255,255,0.3)', borderLeft: '2px solid rgba(255,255,255,0.3)',
          borderRadius: '2px 0 0 0',
        }} />
      </div>

      {/* Video or waiting */}
      <div style={{
        position: 'relative', width: '100%', height: videoHeight,
        background: '#000', overflow: 'hidden',
      }}>
        {state === 'connected' && displayStream ? (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <div style={{
            width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#64748b', fontSize: 12,
          }}>
            Esperando al cliente...
          </div>
        )}

        {/* Top buttons: expand toggle + open fullscreen */}
        <div style={{
          position: 'absolute', top: 6, right: 6, display: 'flex', gap: 4,
        }}>
          <button
            onClick={toggleExpand}
            style={{
              width: 26, height: 26, borderRadius: 6, border: 'none', cursor: 'pointer',
              background: 'rgba(0,0,0,0.5)', color: '#e2e8f0',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            title={isExpanded ? 'Compactar' : 'Agrandar'}
          >
            {isExpanded ? <Shrink size={12} /> : <Expand size={12} />}
          </button>
          <button
            onClick={() => navigate(`/remote-assist/${activeSession.id}`)}
            style={{
              width: 26, height: 26, borderRadius: 6, border: 'none', cursor: 'pointer',
              background: 'rgba(0,0,0,0.5)', color: '#e2e8f0',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            title="Abrir pantalla completa"
          >
            <Maximize2 size={12} />
          </button>
        </div>

        {/* Screen share indicator */}
        {clientScreenSharing && (
          <div style={{
            position: 'absolute', top: 6, left: 6, fontSize: 10, color: '#3b82f6',
            background: 'rgba(0,0,0,0.6)', padding: '2px 6px', borderRadius: 4,
            display: 'flex', alignItems: 'center', gap: 3,
          }}>
            <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#3b82f6' }} />
            Pantalla
          </div>
        )}
      </div>

      {/* Controls */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 12px', background: '#0f172a',
        borderRadius: '0 0 16px 16px',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {activeSession.contact_name || 'Sesion'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#64748b' }}>
            {state === 'connected' && (
              <>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#10b981' }} />
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatTime(elapsed)}</span>
              </>
            )}
            {clientMuted && <span style={{ color: '#f59e0b' }}>Mic off</span>}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            onClick={toggleMic}
            style={{
              width: 32, height: 32, borderRadius: '50%', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: isMuted ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.1)',
              color: isMuted ? '#ef4444' : '#e2e8f0',
            }}
          >
            {isMuted ? <MicOff size={14} /> : <Mic size={14} />}
          </button>
          <button
            onClick={toggleVideo}
            style={{
              width: 32, height: 32, borderRadius: '50%', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: isVideoOff ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.1)',
              color: isVideoOff ? '#ef4444' : '#e2e8f0',
            }}
          >
            {isVideoOff ? <VideoOff size={14} /> : <Video size={14} />}
          </button>
          {availableCameras && availableCameras.length > 1 && (
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowCamPicker(v => !v)}
                title="Cambiar camara"
                style={{
                  width: 32, height: 32, borderRadius: '50%', border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'rgba(255,255,255,0.1)', color: '#e2e8f0',
                }}
              >
                <Camera size={14} />
              </button>
              {showCamPicker && (
                <div style={{
                  position: 'absolute', bottom: 40, right: 0,
                  background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 10, padding: 6, minWidth: 220, zIndex: 20,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                }}>
                  {availableCameras.map((c, i) => (
                    <button
                      key={c.deviceId || i}
                      onClick={() => { switchCamera(c.deviceId); setShowCamPicker(false); }}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left',
                        padding: '6px 10px', borderRadius: 6, border: 'none', cursor: 'pointer',
                        fontSize: 12, color: '#e2e8f0',
                        background: c.deviceId === selectedCameraId ? 'rgba(59,130,246,0.2)' : 'transparent',
                      }}
                    >
                      {c.label || `Camara ${i + 1}`}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <button
            onClick={endSession}
            style={{
              width: 32, height: 32, borderRadius: '50%', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: '#ef4444', color: '#fff',
            }}
          >
            <PhoneOff size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
